/**
 * Diễn đàn thảo luận của 1 môn (plan 2026-10-03 §2) — dùng ở tab "Thảo luận" trang môn của
 * sinh viên (`/my-courses/:slug?tab=forum`) và trang môn admin (`/admin/courses/:slug?tab=forum`).
 * Chủ đề đang mở nằm trên URL `?thread=<id>` → link từ thông báo mở thẳng đúng chủ đề.
 */
import { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Pagination,
  Result,
  Segmented,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CheckCircleFilled,
  CommentOutlined,
  LockOutlined,
  PlusOutlined,
  PushpinOutlined,
} from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/vi";
import { type ForumFilter, forumErrorMessage, useCreateThread, useForumThreads } from "@/api/forum";
import { useAuthStore } from "@/stores/auth";
import { ForumEditor } from "./ForumEditor";
import { ForumThreadView } from "./ForumThreadView";

dayjs.extend(relativeTime);

const { Text } = Typography;

function NewThreadModal({
  slug,
  open,
  onClose,
  onCreated,
}: {
  slug: string;
  open: boolean;
  onClose: () => void;
  onCreated: (id: number) => void;
}) {
  const [form] = Form.useForm<{ title: string; body: string; is_question: boolean }>();
  const create = useCreateThread(slug);
  return (
    <Modal
      title="Chủ đề thảo luận mới"
      open={open}
      onCancel={onClose}
      okText="Đăng"
      cancelText="Huỷ"
      confirmLoading={create.isPending}
      onOk={() => form.submit()}
      destroyOnHidden
      width={900}
      maskClosable={false}
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={{ is_question: true }}
        onFinish={(v) =>
          create.mutate(v, {
            onSuccess: (t) => {
              message.success("Đã đăng chủ đề");
              onCreated(t.id);
            },
            onError: (e) => message.error(forumErrorMessage(e, "Không đăng được chủ đề")),
          })
        }
      >
        <Form.Item
          name="title"
          label="Tiêu đề"
          rules={[{ required: true, message: "Nhập tiêu đề" }]}
        >
          <Input maxLength={200} placeholder="Tóm tắt câu hỏi / vấn đề" />
        </Form.Item>
        <Form.Item
          name="body"
          label="Nội dung"
          rules={[
            {
              validator: (_, v?: string) =>
                (v ?? "").trim() ? Promise.resolve() : Promise.reject(new Error("Nhập nội dung")),
            },
          ]}
        >
          <ForumEditor
            height={320}
            placeholder="Mô tả chi tiết câu hỏi… gõ “/” để chèn bảng, code, công thức"
          />
        </Form.Item>
        <Form.Item name="is_question" valuePropName="checked">
          <Checkbox>Đây là câu hỏi (có thể đánh dấu "đã giải đáp")</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export function CourseForum({ courseSlug }: { courseSlug: string }) {
  const enabled = useAuthStore((s) => !!s.user?.feature_flags?.forum);
  const [params, setParams] = useSearchParams();
  const threadId = params.get("thread") ? Number(params.get("thread")) : null;
  const [filter, setFilter] = useState<ForumFilter>("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const list = useForumThreads(courseSlug, { q, filter, page }, enabled && threadId == null);

  const openThread = (id: number | null) => {
    const next = new URLSearchParams(params);
    if (id == null) next.delete("thread");
    else next.set("thread", String(id));
    setParams(next);
  };

  if (!enabled) return <Alert type="info" showIcon message="Diễn đàn chưa được bật." />;
  if (threadId != null)
    return <ForumThreadView threadId={threadId} onBack={() => openThread(null)} />;
  if (list.error) {
    return (
      <Result
        status="403"
        title="Bạn không có quyền vào diễn đàn môn này"
        subTitle="Diễn đàn dành cho sinh viên đã đăng ký môn và giảng viên phụ trách."
      />
    );
  }

  const data = list.data;
  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Tìm trong diễn đàn"
            onSearch={(v) => {
              setQ(v);
              setPage(1);
            }}
            style={{ width: 260 }}
          />
          <Segmented<ForumFilter>
            value={filter}
            onChange={(v) => {
              setFilter(v);
              setPage(1);
            }}
            options={[
              { label: "Tất cả", value: "all" },
              { label: "Chưa giải đáp", value: "unanswered" },
              { label: "Của tôi", value: "mine" },
              { label: "Đang theo dõi", value: "following" },
            ]}
          />
        </Space>
        {data?.permissions.can_post && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
            Đặt câu hỏi / chủ đề mới
          </Button>
        )}
      </Space>
      {data && !data.permissions.can_post && (
        <Alert
          type="info"
          showIcon
          message="Bạn chỉ có quyền xem diễn đàn môn này (không còn học môn)."
        />
      )}

      {list.isLoading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : !data?.results.length ? (
        <Empty
          description={
            q || filter !== "all"
              ? "Không có chủ đề phù hợp"
              : "Chưa có chủ đề nào — hãy là người đặt câu hỏi đầu tiên"
          }
        />
      ) : (
        <>
          <List
            itemLayout="vertical"
            dataSource={data.results}
            renderItem={(t) => (
              <List.Item
                key={t.id}
                onClick={() => openThread(t.id)}
                style={{ cursor: "pointer", padding: "12px 8px" }}
                extra={
                  <Space direction="vertical" align="end" size={2}>
                    <Text>
                      <CommentOutlined /> {t.reply_count}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(t.last_activity_at).locale("vi").fromNow()}
                    </Text>
                  </Space>
                }
              >
                <Space direction="vertical" size={2}>
                  <Space wrap size={6}>
                    {t.is_pinned && <PushpinOutlined style={{ color: "#1677ff" }} />}
                    {t.is_locked && <LockOutlined />}
                    <Text strong style={{ fontSize: 15 }} delete={t.is_deleted}>
                      {t.title}
                    </Text>
                    {t.is_question &&
                      (t.is_answered ? (
                        <Tag color="success" icon={<CheckCircleFilled />}>
                          Đã giải đáp
                        </Tag>
                      ) : (
                        <Tag color="orange">Chưa giải đáp</Tag>
                      ))}
                    {t.is_deleted && <Tag>{t.deleted_label}</Tag>}
                  </Space>
                  {t.excerpt && (
                    <Text type="secondary" ellipsis style={{ maxWidth: 720 }}>
                      {t.excerpt}
                    </Text>
                  )}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t.author.name}
                    {t.author.is_staff && " (giảng viên)"} ·{" "}
                    {dayjs(t.created_at).format("DD/MM/YYYY")}
                  </Text>
                </Space>
              </List.Item>
            )}
          />
          {data.count > data.page_size && (
            <Pagination
              current={page}
              pageSize={data.page_size}
              total={data.count}
              onChange={setPage}
              style={{ textAlign: "center" }}
            />
          )}
        </>
      )}
      <NewThreadModal
        slug={courseSlug}
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id) => {
          setCreating(false);
          openThread(id);
        }}
      />
    </Space>
  );
}
